<template>
  <div class="v-albums-info">
    <div class="black-mask">
      <breadcrumb type="explore-info" :extra="albumData.albumTitleDisplay" />

      <div class="v-albums-info__content g-content">
        <div class="main">
          <loading v-if="isLoading" />
          <div class="main__content" v-else>
            <div class="main__content__left">
              <div class="cover">
                <!-- <img class="cover__img" src="@/assets/album/2.jpeg" /> -->
                <div class="cover__img" :style="{ 'background-image': `url('${imageSource(albumData.artworkInfo)}')` }">
                </div>
              </div>

              <div class="tablet-show">
                <relatived-albums type="Albums" :data="albumData" :albumUid="albumData.albumUid" />
              </div>
            </div>
            <div class="main__content__right">
              <playlists-albums-info type="ALBUM" :data="albumData"
                :tracksLength="albumData?.tracks ? albumData?.tracks.length : 0" />

              <my-music-list :config="listConfig" :contentData="albumData.tracks" :padding="false" :max100="true"
                :showHeader="false" />

              <div class="mobile-show">
                <relatived-albums type="Albums" :data="albumData" />
              </div>
            </div>
          </div>
        </div>

        <div class="btn-wrap">
          <router-link class="btn" :to="{
              name: 'search',
              query: {
                type: 'albums',
                keyword: albumData.albumTitleDisplay,
                uid: $route.params.id,
              },
            }">
            <span class="btn__text">{{ $t('global.learnMore') }}</span>
          </router-link>
        </div>
      </div>
      <site-map />
    </div>
  </div>
</template>
<script>
import { defineComponent, computed, ref, provide, watch } from 'vue';
import { useRouter, useRoute, onBeforeRouteLeave } from 'vue-router';

import { useI18n } from 'vue-i18n';
import { cloneDeep } from 'lodash-es';
import PlaylistsAlbumsInfo from '@/components/Explore/PlaylistsAlbumsInfo.vue';
import MyMusicList from '@/components/MyMusic/MyMusicList/index.vue';
import {
  contentData,
  functionConfig,
  mobileListConfig,
  pcListConfig,
  musicDetailColumns,
  musicDetailContentData,
  trackDivideColumns,
  trackDivideData,
  mobileListDetailColumns,
  pcListDetailColumns,
  listDetailContentData,
} from '@/config/albumsInfoConfig';
import RelativedAlbums from '@/components/Explore/RelativedAlbums.vue';
import Breadcrumb from '@/views/Explore/Breadcrumb.vue';
import { useSiteStore } from '@/store/site';
import SiteMap from '@/views/Home/SiteMap.vue';
import { getAlbumAPI } from '@/api/getAlbums';
import useRequest from '@/hooks/useRequest';
import imageSource from '@/utils/imageSource';
import usePopInfo from '@/hooks/usePopInfo';
import useMeta from '@/hooks/useMeta';

export default defineComponent({
  name: 'AlbumsInfo',
  components: {
    // PlaylistsAlbumsSidebar,
    PlaylistsAlbumsInfo,
    MyMusicList,
    RelativedAlbums,
    Breadcrumb,
    SiteMap,
  },
  setup() {
    const { updateDynamicMeta, handleMeta } = useMeta();
    handleMeta();
    const { t } = useI18n();
    const route = useRoute();
    const router = useRouter();
    const siteStore = useSiteStore();
    const { errorPopInfo } = usePopInfo();
    const albumData = ref({});
    const listContentConfig = ref({
      notice: '',
      expandDetail: 'music-detail', // list-detail, music-detail, ''
      moreDesc: true,
      contentRowTablet78: true,
    });

    const { request, isLoading, response } = useRequest(getAlbumAPI);

    const handleRequest = () => {
      request({ in: route.params.id });
    };
    handleRequest();

    watch(
      () => route.params.id,
      () => {
        if (route.name === 'explore-albums-info') {
          handleRequest();
        }
      },
    );

    const musicDetail = ref({
      versionAndEdit: {
        columns: musicDetailColumns,
        data: musicDetailContentData,
      },
      track: {
        columns: trackDivideColumns,
        data: trackDivideData,
      },
      // mobile 點擊 row 展開的 key
      mobileExpandKey: 'detail',
    });

    const tabletPcListConfig = pcListConfig('tablet');
    const smPcPcListConfig = pcListConfig('sm-pc');

    const listConfig = computed(() => {
      if (siteStore.deviceType.includes('sm-pc')) {
        return smPcPcListConfig;
      }

      if (siteStore.deviceType.includes('tablet')) {
        return tabletPcListConfig;
      }

      return mobileListConfig;
    });

    const tabletPcListDetailColumns = pcListDetailColumns('tablet');
    const smPcListDetailColumns = pcListDetailColumns('sm-pc');

    const listDetailColumns = computed(() => {
      if (siteStore.deviceType.includes('sm-pc')) {
        return smPcListDetailColumns;
      }

      if (siteStore.deviceType.includes('tablet')) {
        return tabletPcListDetailColumns;
      }

      return mobileListDetailColumns;
    });

    provide('listContentConfig', () => listContentConfig.value);
    provide('musicDetail', musicDetail);
    provide('detailConfig', () => ({
      listDetailColumns: listDetailColumns.value,
      listDetailContentData,
    }));

    watch(response, () => {
      const [err, res] = response.value;

      if (err) {
        errorPopInfo({
          desc: err,
          callback: () => {
            router.back();
            // router.push({ name: 'explore-type', params: { type: 'albums' } });
          },
        });
        return;
      }

      const mainMusicMapping = res.getAlbum.tracks.reduce((obj, item) => {
        if (item.isMasterVersion) {
          obj[String(item.trackNumber)] = [];
          obj[String(item.trackNumber)].push(item);
        } else {
          // obj[String(item.mainTrackNumber)].push(item);
        }

        return obj;
      }, {});

      res.getAlbum.tracks = res.getAlbum.tracks
        .filter((item) => item.isMasterVersion)
        .map((item) => {
          item.musicDetailList = mainMusicMapping[String(item.trackNumber)];
          item.albumUid = res.getAlbum.albumUid;
          return item;
        });

      const data = cloneDeep(res.getAlbum);
      delete data.tracks;
      delete data.tags;

      res.getAlbum.tracks = res.getAlbum.tracks.map((item) => ({
        ...data,
        ...item,
      }));

      albumData.value = res.getAlbum;

      const albumCover = imageSource(albumData.value.artworkInfo);
      siteStore.setBackgroundImage(albumCover);

      // console.log('...', `${t('explore.type.albums')}-${res.getAlbum.albumTitleDisplay}`);

      const updateMetaList = [
        {
          name: 'title',
          // `${t('explore.type.albums')}-${res.getAlbum.albumTitleDisplay}`
          content: `${t('explore.type.albums')} ${res.getAlbum.albumTitleDisplay} | ${t('global.pointCulture')}`,
        },
        {
          name: 'description',
          content: res.getAlbum.albumDescription,
        },
        {
          name: 'image',
          content: albumCover,
        },
      ];

      updateDynamicMeta(updateMetaList);
    });

    // provide('albumData', () => {
    //   const data = cloneDeep(albumData.value);
    //   delete data.tracks;
    //   delete data.tags;
    //   return data;
    // });

    // 離開此頁時，清除背景
    onBeforeRouteLeave(() => {
      siteStore.setBackgroundImage(false);
    });

    const toSearchPage = () => {
      router.push({
        name: 'search',
        query: {
          type: 'albums',
          uid: route.params.id,
        },
      });
    };

    return {
      functionConfig,
      listConfig,
      contentData,
      toSearchPage,
      albumData,
      imageSource,
      isLoading,
    };
  },
});
</script>
<style lang="scss" scoped>
@import '~@/styles/share/blackMask.scss';

.v-albums-info {
  box-sizing: border-box;
  padding-top: 60px;
  padding-bottom: 50px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  // background-image: url('~@/assets/album/8.jpeg');
  width: 100%;
  /* @include h100(); */
  height: 100%;
  overflow-y: auto;

  &--bg {
    &--licensing {
      background-image: url('~@/assets/licensing/bg.png');
    }

    &--front-contact {
      background-image: url('~@/assets/contact/bg.png');
    }
  }

  &__content {}
}

.back-search-wrap {
  display: none;
}

.main {
  background-color: $c-white3;
  border-radius: 10px;
  position: relative;
  margin-top: 16px;

  &__content {
    @include padding(15px);

    &__left {}

    &__right {}
  }
}

.cover {
  text-align: center;
  max-width: 330px;
  margin: 0 auto;

  &__img {
    width: 100%;
    padding-bottom: 100%;
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;

    border-radius: 5px;
  }
}

.popular-genres-box-list {
  margin-top: 8px;

  &__item {
    &+& {
      margin-top: 20px;
    }

    &__box {}
  }
}

.btn-wrap {
  @include flex(center);
  margin-top: 50px;
  padding-bottom: 100px;
}

.btn {
  border: 1px solid $c-white;
  border-radius: 20px;
  @include flex(center);
  height: 40px;
  background-color: transparent;
  @include padding(0 18px);
  cursor: pointer;

  &__text {
    @include font-style($c-white, 14, 700, 0.7px, 18px);
    position: relative;
    z-index: 10;
  }
}

.tablet-show {
  display: none;
}

@media screen and (min-width: $tablet) {
  // .mobile-show {
  //   display: none;
  // }

  // .tablet-show {
  //   display: block;
  // }

  .v-albums-info {
    padding-top: 80px;
    // padding-bottom: 150px;
    padding-bottom: 0;
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;
    // background-image: url('~@/assets/album/8.jpeg');

    &--bg {
      &--licensing {
        background-image: url('~@/assets/licensing/bg.png');
      }

      &--front-contact {
        background-image: url('~@/assets/contact/bg.png');
      }
    }
  }

  // .back-search-wrap {
  //   @include flex(space-between);
  // }

  .back-search {
    margin-top: 80px;

    &__text {
      @include font-style($c-white, 14, 600, 0.8px, 18px);
    }

    &__img {
      display: inline-block;
      margin-left: 12px;

      &--left {
        margin-right: 12px;
        margin-left: 0px;
        transform: rotate(180deg);
      }
    }
  }

  // .main {
  //   background-color: $c-white3;
  //   border-radius: 10px;
  //   @include padding(73px 0);
  //   position: relative;
  //   margin-top: 16px;

  //   &__content {
  //     @include max-width(1300);
  //     @include flex(flex-start, flex-start);

  //     &__left {
  //       flex: none;
  //       width: 330px;
  //     }

  //     &__right {
  //       flex: 1;
  //       margin-left: 64px;
  //     }
  //   }
  // }

  .popular-genres-box-list {
    margin-top: 8px;

    &__item {
      &+& {
        margin-top: 20px;
      }

      &__box {}
    }
  }

  .btn-wrap {
    margin-top: 70px;
  }
}

@media screen and (min-width: $sm-pc) {

  .main {
    background-color: $c-white3;
    border-radius: 10px;
    @include padding(73px 0);
    position: relative;
    margin-top: 16px;

    &__content {
      @include max-width(1300);
      @include flex(flex-start, flex-start);

      &__left {
        flex: none;
        width: 330px;
      }

      &__right {
        flex: 1;
        margin-left: 64px;
      }
    }
  }

  .mobile-show {
    display: none;
  }

  .tablet-show {
    display: block;
  }

  .back-search-wrap {
    @include flex(space-between);
  }

}
</style>
